import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";

import { cn } from "@/utils/utils";
import MarkdownRenderer from "@/components/ui/markdown-renderer";

const TooltipProvider = TooltipPrimitive.Provider;

const Tooltip = TooltipPrimitive.Root;

const TooltipTrigger = TooltipPrimitive.Trigger;

const TOOLTIP_MAX_WIDTH = {
  13: "max-w-52",
  15: "max-w-60",
  18: "max-w-72",
  20: "max-w-80",
  24: "max-w-96"
} as const;

type TooltipMaxWidth = keyof typeof TOOLTIP_MAX_WIDTH;

interface TooltipContentProps
  extends React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content> {
  enableMarkdown?: boolean;
  /** Max width in rem, from design's tooltip widths. Unset means no limit. */
  maxWidth?: TooltipMaxWidth;
}

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  TooltipContentProps
>(
  (
    {
      className,
      sideOffset = -6,
      side,
      enableMarkdown = true,
      maxWidth,
      ...props
    },
    ref
  ) => {
    const renderContent = () => {
      if (enableMarkdown && typeof props.children === "string") {
        return (
          <MarkdownRenderer
            content={props.children as string}
            className="bodySecondary"
          />
        );
      }
      return props.children;
    };

    return (
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          ref={ref}
          sideOffset={sideOffset}
          side={side}
          className={cn(
            "z-50 overflow-hidden rounded-lg border-0 bg-surface-elevation3 px-3 py-2 text-base text-primary shadow-tooltip",
            "data-[state=delayed-open]:animate-fadeIn data-[state=closed]:animate-fadeOut",
            maxWidth && TOOLTIP_MAX_WIDTH[maxWidth],
            className
          )}
          {...props}
        >
          <div className="z-20">{renderContent()}</div>
          <TooltipPrimitive.Arrow
            height={8}
            width={15}
            className="fill-surface-elevation3"
          ></TooltipPrimitive.Arrow>
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    );
  }
);
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
